/**
 * Shakebugs Feedback Service
 *
 * Singleton service for Shakebugs bug reporting and user feedback.
 * This is a user-initiated feedback tool, NOT analytics - therefore
 * it does not require cookie consent.
 */

import * as Sentry from '@sentry/nextjs'
import { isE2E, isPrd } from '@shared/util/env'

interface ShakeSDK {
    start(apiKey: string): void
    registerUser(userId: string): void
    unregisterUser(): void
}

function debugLog(...args: unknown[]) {
    if (!isPrd()) {
        console.info('[ShakebugsService]', ...args)
    }
}

class ShakebugsService {
    private initialized = false
    private shake: ShakeSDK | null = null
    private pendingUserId: string | null = null

    /**
     * Initialize Shakebugs SDK
     * Should be called once on app mount, client-side only
     */
    async initialize(): Promise<void> {
        // Guard: browser only
        if (typeof window === 'undefined') return

        // Guard: already initialized
        if (this.initialized) return

        // Guard: not enabled
        if (!this.isEnabled()) {
            debugLog('Disabled via environment')
            return
        }

        const apiKey = process.env.NEXT_PUBLIC_SHAKE_API_KEY
        if (!apiKey) {
            debugLog('Missing API key')
            Sentry.captureMessage('Shakebugs API key not configured', {
                level: 'warning',
                tags: {
                    errorType: 'missing_config',
                    service: 'shakebugs',
                },
            })
            return
        }

        try {
            // Dynamic import to avoid SSR issues
            const ShakeModule = await import('@shakebugs/browser')
            this.shake = ShakeModule.default as ShakeSDK

            this.shake.start(apiKey)
            this.initialized = true

            debugLog('Initialized successfully')

            // Register any pending user
            if (this.pendingUserId) {
                this.registerUser(this.pendingUserId)
                this.pendingUserId = null
            }
        } catch (error) {
            if (!isPrd()) {
                console.error('[ShakebugsService] Failed to initialize:', error)
            }
            Sentry.captureException(error, {
                tags: {
                    errorType: 'initialization_failed',
                    service: 'shakebugs',
                },
            })
        }
    }

    /**
     * Register the current user with Shakebugs
     * Links feedback to specific users in the Shake dashboard
     */
    registerUser(userId: string): void {
        if (!userId) return

        if (!this.initialized || !this.shake) {
            // Store for later when initialized
            this.pendingUserId = userId
            debugLog('Queued user registration:', userId)
            return
        }

        try {
            this.shake.registerUser(userId)
            debugLog('User registered:', userId)
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'register_user_failed',
                    service: 'shakebugs',
                },
            })
        }
    }

    /**
     * Unregister the current user (e.g., on logout)
     */
    unregisterUser(): void {
        if (!this.initialized || !this.shake) return

        try {
            this.shake.unregisterUser()
            this.pendingUserId = null
            debugLog('User unregistered')
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'unregister_user_failed',
                    service: 'shakebugs',
                },
            })
        }
    }

    /**
     * Check if service is initialized
     */
    isInitialized(): boolean {
        return this.initialized
    }

    /**
     * Check if Shakebugs should be enabled in current environment
     */
    private isEnabled(): boolean {
        // Never enable in E2E tests
        if (isE2E()) return false

        // Check environment variable
        return process.env.NEXT_PUBLIC_SHAKE_ENABLED === 'true'
    }
}

// Export singleton instance
const shakebugsService = new ShakebugsService()
export default shakebugsService
